import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { markRecommendationFiltersAdjusted } from "../../stores/recommendations/mentorRecommendationsSlice";
import {
  filterGraduateSchoolsEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { useTaggedResourceTiles } from "../../components/ResourcePage/helpers/useTaggedResourceTiles";
import {
  filterResourceTileGroupsByQuery,
  filterResourceTileGroupsByTag,
} from "../../components/ResourcePage/helpers/filterResourceTileGroups";
import ResourcePageHeader from "../../components/ResourcePage/ResourcePageHeader";
import ResourceTilesGrid from "../../components/ResourcePage/ResourceTileGrid";
import { ResourceFilterPopover } from "../../components/FilterPopover/FilterPopover";
import { Search } from "../../components/Search/Search";
import { FilterPillRow } from "../../components/FilterPillRow/FilterPillRow";
import { SortPopover } from "../../components/SortPopover/SortPopover";
import { sortResourceTiles } from "../../utils/resourceSorting";
import type { RecommenderOutletContext } from "../RecommenderPage/recommenderPageTypes";

const DEGREE_TAG_FILTERS = [
  "Degree General Resources",
  "Degree Additional Resources",
];

const FindGraduateSchools = () => {
  const dispatch = useAppDispatch();

  const { userId, sessionId } = useAppSelector((state) => state.session);

  const { pageFilters, setPageFilters, sortSelections, setSortSelections } =
    useOutletContext<RecommenderOutletContext>();

  const [query, setQuery] = useState<string>("");

  const recommendations = useAppSelector(
    (state) => state.mentorRecommendations.data,
  );

  const applyRecommendations =
    recommendations && !recommendations.recommendedFilters.adjusted;
  const selectedDegrees = applyRecommendations
    ? recommendations.recommendedFilters.degrees
    : pageFilters.findGraduateSchools.degrees;
  const selectedSubfields = applyRecommendations
    ? recommendations.recommendedFilters.subFields
    : pageFilters.findGraduateSchools.subfields;

  const taggedResourceTiles = useTaggedResourceTiles(DEGREE_TAG_FILTERS);

  const filteredResourceTiles = useMemo(
    () =>
      filterResourceTileGroupsByTag(taggedResourceTiles, {
        degrees: selectedDegrees,
        subfields: selectedSubfields,
      }),
    [taggedResourceTiles, selectedDegrees, selectedSubfields],
  );

  const queriedResourceTiles = useMemo(
    () => filterResourceTileGroupsByQuery(filteredResourceTiles, query),
    [filteredResourceTiles, query],
  );

  const sortedResourceTiles = useMemo(
    () =>
      sortResourceTiles(
        queriedResourceTiles,
        sortSelections.findGraduateSchools,
      ),
    [queriedResourceTiles, sortSelections.findGraduateSchools],
  );

  const tileCount = sortedResourceTiles
    .map((group) => group.data.length)
    .reduce((acc, e) => acc + e, 0);

  const updateGraduateSchoolFilters = useCallback(
    (updates: { subfields?: string[]; degrees?: string[] }) => {
      setPageFilters((prev) => ({
        ...prev,
        findGraduateSchools: {
          ...prev.findGraduateSchools,
          ...updates,
        },
      }));
    },
    [setPageFilters],
  );

  const handleSubfieldsChange = useCallback(
    (subfields: string[]) => {
      dispatch(markRecommendationFiltersAdjusted());
      updateGraduateSchoolFilters({ subfields });
    },
    [dispatch, updateGraduateSchoolFilters],
  );

  const handleDegreesChange = useCallback(
    (degrees: string[]) => {
      dispatch(markRecommendationFiltersAdjusted());
      updateGraduateSchoolFilters({ degrees });
    },
    [dispatch, updateGraduateSchoolFilters],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(markRecommendationFiltersAdjusted());
    updateGraduateSchoolFilters({
      subfields: [],
      degrees: [],
    });
  }, [dispatch, updateGraduateSchoolFilters]);

  useEffect(() => {
    if (!userId || !sessionId) return;

    const allFilteredTiles = filteredResourceTiles
      .map((tile) => tile.data)
      .flat();
    const allTileTitles = allFilteredTiles.map((tile) => tile.title);

    const eventData = filterGraduateSchoolsEventBuilder(
      userId,
      sessionId,
      allTileTitles,
      {
        degreeFilter: selectedDegrees,
        subfieldsFilter: selectedSubfields,
      },
    );

    void dispatch(sendEvent(eventData));
  }, [
    userId,
    sessionId,
    selectedSubfields,
    filteredResourceTiles,
    selectedDegrees,
    query,
    dispatch,
  ]);

  return (
    <>
      <ResourcePageHeader
        title="Graduate School Resources"
        tileCount={tileCount}
        style={{ marginBottom: "1rem" }}
      />
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <Search
          handleSearchChange={(e) => {
            setQuery(e.target.value);
          }}
          query={query}
        />
        <ResourceFilterPopover
          showDegree
          showSubfield
          selectedDegrees={selectedDegrees}
          selectedSubfields={selectedSubfields}
          onDegreesChange={handleDegreesChange}
          onSubfieldsChange={handleSubfieldsChange}
          onClear={handleClearFilters}
        />
        <SortPopover
          option={sortSelections.findGraduateSchools}
          onChange={(option) => {
            setSortSelections({
              ...sortSelections,
              findGraduateSchools: option,
            });
          }}
        />
      </div>
      <FilterPillRow
        filters={[...selectedDegrees, ...selectedSubfields]}
        onClear={(filter) => {
          handleDegreesChange(
            selectedDegrees.filter((value) => value !== filter),
          );
          handleSubfieldsChange(
            selectedSubfields.filter((value) => value !== filter),
          );
        }}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-8)",
        }}
        data-cy={"graduate-school-resources"}
      >
        {sortedResourceTiles.map((group) => (
          <ResourceTilesGrid
            key={group.tagFilter}
            resourceTiles={group.data}
            bookmarkSource="FindGraduateSchoolsTile"
          />
        ))}
      </div>
    </>
  );
};

export default FindGraduateSchools;
